import {
  Text,
  Group,
  Box,
  Avatar,
  Badge,
  Table,
} from '@mantine/core';
import { MapPin, Clock } from 'react-feather';
import type { Coach } from '../../../types';
import { getCoachInitials, getCoachDisplayName } from '../../../utils/coachHelpers';
import { getAvatarColor } from '../../../utils/avatarHelpers';
import { medleyEmailOf } from '../../../utils/medleyEmail';
import {
  NO_MEDLEY_EMAIL_BADGE_STYLES,
  NO_MEDLEY_EMAIL_LABEL,
} from '../../../components/coaches/noMedleyEmailBadge';
import {
  SortableTableHeader,
  CoachSatisfactionRating,
  CoachStatusPill,
  type SortConfig,
} from '../../../components/common';
import type { ColumnConfig } from '../../../hooks';

// Format timezone for display (e.g., "America/Chicago" -> "Central Time")
const TIMEZONE_MAP: Record<string, string> = {
  'America/New_York': 'Eastern Time',
  'America/Chicago': 'Central Time',
  'America/Denver': 'Mountain Time',
  'America/Los_Angeles': 'Pacific Time',
  'America/Anchorage': 'Alaska Time',
  'Pacific/Honolulu': 'Hawaii Time',
  'Europe/London': 'GMT',
  'Europe/Paris': 'CET',
  'Europe/Berlin': 'CET',
  'Asia/Tokyo': 'JST',
  'Asia/Shanghai': 'CST',
  'Asia/Singapore': 'SGT',
  'Australia/Sydney': 'AEST',
};

export function formatTimezone(tz: string): string {
  return TIMEZONE_MAP[tz] || tz.split('/').pop()?.replace(/_/g, ' ') || tz;
}

/** Render a status pill badge for a coach (MED-683: derived Active/Inactive with tooltip). */
export function renderStatusPill(coach: Pick<Coach, 'status' | 'modality_approvals'>) {
  return (
    <CoachStatusPill
      status={coach.status}
      modalityApprovals={coach.modality_approvals ?? []}
    />
  );
}

/** Render a facilitator yes/no pill */
export function renderFacilitatorPill(isFacilitator: boolean) {
  if (!isFacilitator) return <Text size="sm" c="dimmed">—</Text>;
  return (
    <Badge
      size="xs"
      style={{
        backgroundColor: 'rgba(76, 175, 80, 0.12)',
        color: '#2E7D32',
        padding: '2px 8px',
        borderRadius: '3px',
        fontSize: '11px',
        fontWeight: 500,
      }}
    >
      YES
    </Badge>
  );
}

/** Render certification badges (max 2 + overflow count) */
export function renderCertifications(certifications: string[]) {
  if (!certifications || certifications.length === 0) {
    return <Text size="sm" c="dimmed">—</Text>;
  }
  const visible = certifications.slice(0, 2);
  const overflow = certifications.length - 2;
  return (
    <Group gap={4} wrap="nowrap">
      {visible.map((cert) => (
        <Badge key={cert} variant="light" color="gray" size="xs">
          {cert}
        </Badge>
      ))}
      {overflow > 0 && (
        <Text size="xs" c="dimmed">
          +{overflow}
        </Text>
      )}
    </Group>
  );
}

/** Render a sortable or static column header */
export function renderColumnHeader(
  column: ColumnConfig,
  sortConfig: SortConfig | null,
  onSort: (key: string) => void,
  headerClassName: string,
) {
  if (column.sortable) {
    return (
      <Table.Th key={column.id} className={headerClassName}>
        <SortableTableHeader
          label={column.label.toUpperCase()}
          sortKey={column.id}
          currentSort={sortConfig}
          onSort={onSort}
        />
      </Table.Th>
    );
  }
  return (
    <Table.Th key={column.id} className={headerClassName}>
      <Text size="xs" fw={600} c="dimmed" tt="uppercase">
        {column.label}
      </Text>
    </Table.Th>
  );
}

/** Render cell content for a given column and coach */
export function renderCellContent(column: ColumnConfig, coach: Coach) {
  switch (column.id) {
    case 'name': {
      const bgColor = getAvatarColor(`${coach.first_name || ''}${coach.last_name || ''}`);
      return (
        <Group gap="sm" wrap="nowrap">
          <Avatar
            size={40}
            radius="xl"
            src={coach.avatar_url}
            styles={{
              root: { backgroundColor: bgColor },
              placeholder: { color: '#FFFFFF', backgroundColor: bgColor },
            }}
          >
            {getCoachInitials(coach)}
          </Avatar>
          <Box>
            <Group gap={4} wrap="nowrap">
              <Text fw={500} size="sm">
                {getCoachDisplayName(coach)}
              </Text>
              {coach.pronouns && (
                <Text size="xs" c="dimmed">
                  ({coach.pronouns})
                </Text>
              )}
              {coach.coach_type === 'partner' && (
                <Badge
                  size="xs"
                  variant="light"
                  color="blue"
                  style={{
                    textTransform: 'uppercase',
                    fontSize: '9px',
                    fontWeight: 600,
                    letterSpacing: '0.3px',
                  }}
                >
                  Partner
                </Badge>
              )}
              {coach.magic_link_invitation_sent_at &&
                !coach.first_magic_link_login_at && (
                  <Badge
                    size="xs"
                    variant="light"
                    color="warningAmber"
                    style={{
                      textTransform: 'uppercase',
                      fontSize: '9px',
                      fontWeight: 600,
                      letterSpacing: '0.3px',
                    }}
                  >
                    SGOS Invite Sent
                  </Badge>
                )}
            </Group>
            {/* MED-1355: a non-Medley users.email is not the Medley email.
                Show the badge, as on the coach profile. */}
            {medleyEmailOf(coach.email) ? (
              <Text size="xs" c="dimmed">
                {coach.email}
              </Text>
            ) : (
              <Badge
                styles={NO_MEDLEY_EMAIL_BADGE_STYLES}
                title="Needs a Medley email before they can lead a group or be a 1:1 coach"
              >
                {NO_MEDLEY_EMAIL_LABEL}
              </Badge>
            )}
          </Box>
        </Group>
      );
    }
    case 'status':
      return renderStatusPill(coach);
    case 'location':
      return (
        <Group gap={4} wrap="nowrap">
          <MapPin size={14} color="#888" />
          <Text size="sm">{coach.location?.split(',')[0] || '—'}</Text>
        </Group>
      );
    case 'timezone':
      return (
        <Group gap={4} wrap="nowrap">
          <Clock size={14} color="#888" />
          <Text size="sm">{formatTimezone(coach.home_base_timezone)}</Text>
        </Group>
      );
    case 'facilitator':
      return renderFacilitatorPill(coach.facilitator_experience);
    case 'certifications':
      return renderCertifications(coach.certifications || []);
    case 'satisfaction':
      return (
        <CoachSatisfactionRating
          rating={coach.satisfaction_rating}
          surveyCount={coach.satisfaction_survey_count}
        />
      );
    case 'cohorts':
      return (
        <Text size="sm" fw={500}>
          {coach.active_cohort_count} total
        </Text>
      );
    default:
      return <Text size="sm">—</Text>;
  }
}
